import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RxAppSdkService } from './rx-app-sdk.service';
import { TimberService } from '@logging/timber.service';
import { RxService } from './rx.service';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { RoleTypeEnum, RxAppApplicationModeEnum, RxStatus, SoftwareOptionsForCompany } from '@shared/enums';
import { RxComponents } from '../rx-app/readOnlySettings/rx-components-model';
import { GlobalSettingsService } from '@core/globalSettings.service';
import rxComponentsRules from '../rx-app/readOnlySettings/rx-components-rules.json';
import { IOrderInformation } from '@interfaces/IOrderInformation';
import { BaseDestroyable } from '@core/base-destroyable';
import { combineLatest, Observable } from 'rxjs';
import { EupRoutesService, RoutingData } from '@core/eupRoutes.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { RxAppiTeroServicesService } from './rx-app.iteroservices.service';
import { LocalStorageService } from '@core/localStorage.service';
import { AppConfigService } from 'app/services/appConfig/appConfigService';
import { Consts } from '@shared/consts';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { Utils } from '@shared/utils.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { IShellContext } from '@shared/store/IShellContext';


@Injectable({ providedIn: 'root' })
export class RxAppService extends BaseDestroyable {
	private readonly serviceName = 'RxAppService';
	private readonly storage: Storage;

	constructor(
		private globalSettings: GlobalSettingsService,
		private sdkService: RxAppSdkService,
		private logger: TimberService,
		private rxService: RxService,
		private eupRoutesService: EupRoutesService,
		private http: EupHttpHandler,
		private rxAppiTeroServicesService: RxAppiTeroServicesService,
		private readonly localStorageService: LocalStorageService,
		private readonly appConfigService: AppConfigService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly utils: Utils,
		private readonly shellContextService: ShellContextService,
	) {
		super();
		this.storage = localStorageService.storage;
	}

	isRxLaunchViaSLEnabled$(companyId: number): Observable<boolean> {
		return this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId).pipe(
			map(softwareOptions => Object.values(softwareOptions).includes(SoftwareOptionsForCompany.cswo_RxLaunchViaSLOnMIDCDoctor))
		);
	}

	isIntuitiveFlowRxEnabled$(): Observable<boolean> {
		const isNewFlowRxEnabled$ = this.shellContextService.selectedCompanyId$.pipe(
			switchMap((companyId) => this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId)),
			map((softwareOptions) => Object.values(softwareOptions).includes(SoftwareOptionsForCompany.cswo_NewFlowRx)),
			shareReplay(1),
		);

		const isDoctor$ = this.shellContextService.roleType$.pipe(
			map((roleType) => roleType === RoleTypeEnum.Doctor),
			shareReplay(1),
		);

		return combineLatest([isNewFlowRxEnabled$, isDoctor$]).pipe(
			map(([isNewFlowRxEnabled, isDoctor]) => isNewFlowRxEnabled && isDoctor),
		);
	}

	/**
	 * Note: contactId, companyId, languageCode, authInfo come from init-context event
	 * @param routeSnapshot - ActivatedRouteSnapshot from the component (required to get correct route params)
	 */
	buildAppParameters$(routeSnapshot: ActivatedRouteSnapshot): Observable<Record<string, unknown>> {
		return this.shellContextService.getContext().pipe(
			take(1),
			map(shellContext => this.buildAppParametersFromContext(shellContext, routeSnapshot))
		);
	}

	private buildAppParametersFromContext(shellContext: IShellContext, routeSnapshot: ActivatedRouteSnapshot): Record<string, unknown> {
		const { apiEndpoint } = this.appConfigService.appSettings;
		const regionalApiEndpoint = this.storage[Consts.Storage.RegionalApiEndpoint];
		const rxAppApiEndpoint = this.localStorageService.getItemOrEmpty<RoutingData>(Consts.Storage.Routes).rxAppApiEndpoint;
		const patientAppApiEndpoint = this.localStorageService.getItemOrEmpty<RoutingData>(Consts.Storage.Routes).patientAppApiEndpoint;

		const routeParams = this.utils.lowerCaseProps(routeSnapshot.params);
		const queryParams = this.utils.lowerCaseProps(routeSnapshot.queryParams);

		const { orderId, rxId, patientGuid } = this.syncRouteParamsWithShellContext(routeSnapshot, queryParams, shellContext);

		const { isReadOnly, readOnlyRules } = this.getReadOnlyParams(routeParams, queryParams);

		const params: Record<string, unknown> = {
			apiEndpoint: regionalApiEndpoint || apiEndpoint,
			rxAppApiEndpoint,
			patientAppApiEndpoint,
			applicationMode: RxAppApplicationModeEnum.Web,
			isReadOnly,
		};

		if (routeParams?.resendscan == 'true' && routeParams.id) {
			params.isResendScan = true;
			params.clonedFromRxId = routeParams.id;
			params.enableAllCaseTypesForAddRx = true;
			params.readOnlyRules = readOnlyRules;
			return params;
		}

		// Only add optional params if they have values
		if (orderId) {
			params.orderId = orderId;
		}
		if (rxId) {
			params.rxId = rxId;
		}
		if (patientGuid) {
			params.patientGuid = patientGuid;
		}
		if (readOnlyRules) {
			params.readOnlyRules = readOnlyRules;
		}

		return params;
	}

	/**
	 * Syncs route/query params with ShellContext bidirectionally:
	 * - If params exist in route/query, updates ShellContext and returns them
	 * - If params don't exist in route/query, gets them from ShellContext
	 * - When setting orderId, clears rxId in ShellContext (and vice versa) to open via link
	 */
	private syncRouteParamsWithShellContext(
		snapshot: ActivatedRouteSnapshot,
		queryParams: Record<string, any>,
		shellContext: IShellContext
	): { orderId: number | null; rxId: string | null; patientGuid: string | null } {
		const id = snapshot.paramMap.get('id');
		const resendScan = snapshot.paramMap.get('resendScan');
		const patientGuidFromQuery = queryParams['patientguid'];

		let orderId: number | null = null;
		let rxId: string | null = null;
		let patientGuid: string | null = null;

		const updates: Partial<{ orderId: number | null; rxId: string | null; patientId: string }> = {};

		// Handle orderId/rxId from route param 'id'
		if (id) {
			if (isNaN(+id)) {
				if(resendScan) {
					updates.rxId = null;
					updates.orderId = null;
				}
				else {
					rxId = id;
					updates.rxId = id;
					updates.orderId = null;
				}
			} else {
				orderId = +id;
				updates.orderId = +id;
				updates.rxId = null;
			}
		} else {
			// Fallback to ShellContext
			orderId = shellContext?.bizCtx?.orderId || null;
			rxId = shellContext?.bizCtx?.rxId || null;
		}

		// Handle patientGuid from query param
		if (patientGuidFromQuery) {
			patientGuid = patientGuidFromQuery;
			updates.patientId = patientGuidFromQuery;
		} else {
			// Fallback to ShellContext
			patientGuid = shellContext?.bizCtx?.patientId || null;
		}

		// Update ShellContext if we have new values from route/query params
		if (Object.keys(updates).length > 0) {
			this.shellContextService.updateContext(context => {
				Object.assign(context.bizCtx, updates);
			});
		}

		return { orderId, rxId, patientGuid };
	}

	/**
	 * Gets isReadOnly and readOnlyRules from route/query params
	 */
	private getReadOnlyParams(
		routeParams: Record<string, any>,
		queryParams: Record<string, any>
	): { isReadOnly: boolean; readOnlyRules: RxComponents | null } {
		const isTakenByScanner = routeParams['istakenbyscanner'] === 'true';
		const isReadOnlyQueryParam = queryParams['isreadonly'] === 'true';
		const isReferralExist = queryParams['isreferralexist'] === 'true';
		const statusId = +queryParams['statusid'] || 0;
		const isResendScan = routeParams['resendscan'] === 'true';

		if (isResendScan) {
			return { isReadOnly: false, readOnlyRules: rxComponentsRules.resendScan };
		}

		const isScanningCenterWithReferral = this.isScanningCenterWithReferral(isReferralExist);
		const isReadOnly = isScanningCenterWithReferral ? true : (isTakenByScanner || isReadOnlyQueryParam);

		let readOnlyRules: RxComponents | null = this.getReadOnlyRulesForSpecificOrderStatus(statusId, isScanningCenterWithReferral);

		return { isReadOnly, readOnlyRules };
	}

	getRxReferralAndDownloadPdf(rxObj: any) {
		return this.rxService.getRxReferralAndDownloadPdf(rxObj);
	}

	subscribeToNotesEvents(): void {
		this.sdkService.onGetLabNotes()
		.pipe(
			tap((notes) => {
				this.logger.info(`Get Lab Notes: ${JSON.stringify(notes)}`, {module: this.serviceName});
				this.rxService.newNoteObservable$.next(notes);
			}),
			takeUntil(this.componentAlive$)
		).subscribe();
	}

	addNote({ content, createdByName }: { content; createdByName }): void {
		const contactId = this.globalSettings.get().contactId;
		this.sdkService.addNote({ content, createdByName, contactId })
		.pipe(
			tap(noteAddedResponse => {
				this.logger.info(`Add Note: ${JSON.stringify(noteAddedResponse)}`, {module: this.serviceName});
				this.rxService.newNoteObservable$.next([noteAddedResponse]);
				this.sdkService.saveRx();
			}),
			takeUntil(this.componentAlive$)
		).subscribe();
	}

	private getReadOnlyRulesForReferralWorkflowScanningCenter(isScanningCenterWithReferral: boolean, statusId: number): RxComponents {
		return isScanningCenterWithReferral && statusId === RxStatus.RxCreated ? rxComponentsRules.scanningCenterPartial : null;
	}

	private updateReadOnlyRulesForPending(statusId: number): RxComponents {
		return statusId === RxStatus.PendingAttachment ? rxComponentsRules.pendingCase : null;
	}

	private updateReadOnlyRulesForScanning(statusId: number): RxComponents {
		return statusId === RxStatus.Scanning ? rxComponentsRules.scanning : null;
	}

	isScanningCenterWithReferral(isReferralExist: boolean): boolean {
		const isReferralWorkflowScanningCenter = this.globalSettings
			.get()
			.companySoftwareOptions?.find((swo) => swo === SoftwareOptionsForCompany.referralWorkflowScanningCenter);

		return !!isReferralWorkflowScanningCenter && isReferralExist;
	}

	proceedOrderToModeling(orderInformation: IOrderInformation): void {
		this.sdkService
			.updateOrderInformation(orderInformation)
			.pipe(
				switchMap(rxModel => this.saveAndProceedToModeling(JSON.stringify(rxModel), rxModel.Order.ID)),
				tap(rxModel => this.rxAppiTeroServicesService.proceedOrderToModelingSucceed.next(!!rxModel)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	proceedOrderToMilling(orderInformation: IOrderInformation): void {
		this.sdkService
			.updateOrderInformation(orderInformation)
			.pipe(
				switchMap((rxModel) => this.saveAndProceedToMilling(JSON.stringify(rxModel), rxModel.Order.ID)),
				tap((response) => {
					const localUpdatedDate = new Date(Number(response.Result.eventResponse.Utc.replace('/Date(', '').replace(')/', '')));
					this.rxAppiTeroServicesService.proceedOrderToMillingSucceed.next({
						dateUpdated: localUpdatedDate,
						millingNumber: response.Result.savedRx.OrderInformation.NumOfModels,
						additionalDiesPerTooth: response.Result.savedRx.OrderInformation.AdditionalDies,
					});
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	rescanOrder(rescanInformation: { emailAddress: string, rejectionReason: number }): void {
		this.sdkService.saveRx()
			.pipe(
				switchMap(rxModel =>
					this.saveAndRescanOrder(JSON.stringify(rxModel), rxModel.Order.ID, rescanInformation.rejectionReason, rescanInformation.emailAddress)
				),
				tap(result => this.rxAppiTeroServicesService.rescanSucceed.next(result.Result)),
				takeUntil(this.componentAlive$)
			).subscribe();
	}

	subscribeToGetRx(): void {
		this.sdkService.onGetRx()
		.pipe(
			map(rx => rx.OrderInformation),
			tap((orderInformation) => {
				this.logger.info(`Get Order Information: ${JSON.stringify(orderInformation)}`, {module: this.serviceName});
				this.rxService.setOrderInformation({...orderInformation});
			}),
			takeUntil(this.componentAlive$)
		).subscribe();
	}

	getReadOnlyRulesForSpecificOrderStatus(statusId: number, isScanningCenterWithReferral: boolean): RxComponents{
		switch (statusId) {
			case RxStatus.Scanning:
				return this.updateReadOnlyRulesForScanning(statusId);
			case RxStatus.PendingAttachment:
				return this.updateReadOnlyRulesForPending(statusId);
			default:
				return this.getReadOnlyRulesForReferralWorkflowScanningCenter(isScanningCenterWithReferral, statusId);
		}
	}


	private saveAndProceedToModeling(rxFormJson: string, orderId: number): Observable<any> {
		const url = `${this.eupRoutesService.lab.proceedToModeling}`;
		return this.http.post(url, { rxFormJson: rxFormJson, orderId: orderId }, undefined, true, false);
	}

	private saveAndProceedToMilling(rxFormJson: string, orderId: number) {
		const url = `${this.eupRoutesService.lab.proceedToMilling}`;
		return this.http.post(url, { orderId: orderId, rxFormJson: rxFormJson }, undefined, true, false);
	}

	private saveAndRescanOrder(rxFormJson: string, orderId: number, rejectionReason: number, emailAddress: string) {
		const url = `${this.eupRoutesService.lab.backToScanning}`;
		return this.http.post(url, { orderId, rxFormJson, rejectionReason, emailAddress }, undefined, true, false);
	}

}
